import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Search, Loader2, IdCard, ShieldCheck, FileText, Mail, Linkedin, Phone, BellRing, Download,
    ChevronLeft, ChevronRight, User, MapPin, Sparkles, X, ExternalLink, FileDown, Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/Avatar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError, backendUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const PAGE_SIZE = 25;

const FILTERS = [
    { key: "any",              label: "All" },
    { key: "with_passport",    label: "Has passport" },
    { key: "without_passport", label: "No passport" },
    { key: "incomplete",       label: "Under 40%" },
    { key: "with_resume",      label: "Resume on file" },
    { key: "without_resume",   label: "No resume" },
    { key: "id_verified",      label: "ID verified" },
];

const completionTone = (pct) => {
    if (pct >= 70) return "text-emerald-600 bg-emerald-500/10 border-emerald-500/30";
    if (pct >= 40) return "text-sky-600 bg-sky-500/10 border-sky-500/30";
    if (pct > 0)   return "text-amber-600 bg-amber-500/10 border-amber-500/30";
    return "text-slate-500 bg-slate-500/10 border-slate-500/30";
};

export default function AllProfessionals() {
    const [searchParams] = useSearchParams();
    const initialFilter = searchParams.get("filter") || "any";
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState(initialFilter);
    const [loading, setLoading] = useState(true);
    const [reminderDialog, setReminderDialog] = useState(null); // {row}
    const [reminderNote, setReminderNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // Inline resume preview drawer state — {row, blobUrl} while a PDF is loaded
    const [resumeDrawer, setResumeDrawer] = useState(null);
    const [resumeLoading, setResumeLoading] = useState(false);
    // CSV export progress
    const [exporting, setExporting] = useState(false);
    // Delete-passport dialog state (master admin can wipe stale/test passports).
    // Backed by DELETE /admin/passports/{email} — 24-hour soft-delete window.
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Revoke blob URLs on drawer close / unmount to free memory.
    useEffect(() => {
        return () => {
            if (resumeDrawer?.blobUrl) URL.revokeObjectURL(resumeDrawer.blobUrl);
        };
    }, [resumeDrawer?.blobUrl]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page, page_size: PAGE_SIZE };
            if (q) params.q = q;
            if (filter !== "any") params.filter = filter;
            const { data } = await api.get("/admin/all-professionals", { params });
            setRows(data.results || []);
            setTotal(data.total || 0);
        } catch (err) {
            const msg = formatApiError(err);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [page, q, filter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [q, filter]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const fireReminder = async () => {
        if (!reminderDialog?.row) return;
        try {
            setSubmitting(true);
            const { data } = await api.post(`/admin/users/${reminderDialog.row.user_id}/action`, {
                action: "send_passport_reminder",
                reason: reminderNote,
            });
            toast.success(`Reminder queued — ${data.completion_pct}% complete.`);
            setReminderDialog(null);
            setReminderNote("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const firePurge = async () => {
        if (!deleteDialog?.row?.email) return;
        try {
            setDeleting(true);
            await api.delete(`/admin/passports/${encodeURIComponent(deleteDialog.row.email)}`);
            toast.success(`Passport for ${deleteDialog.row.email} soft-deleted (24h undo).`);
            setRows((prev) => prev.filter((r) => r.user_id !== deleteDialog.row.user_id));
            setTotal((t) => Math.max(0, t - 1));
            setDeleteDialog(null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    const openResume = async (row) => {
        if (!row.resume_id) return;
        try {
            setResumeLoading(true);
            setResumeDrawer({ row, blobUrl: null }); // open shell with spinner
            const url = backendUrl(`/api/admin/resumes/${row.resume_id}/download`);
            const token = localStorage.getItem("simonara_admin_token");
            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const objUrl = URL.createObjectURL(blob);
            setResumeDrawer((prev) => (prev && prev.row.user_id === row.user_id ? { row, blobUrl: objUrl } : prev));
        } catch (err) {
            toast.error("Couldn't open the resume file.");
            setResumeDrawer(null);
        } finally {
            setResumeLoading(false);
        }
    };

    const closeResumeDrawer = () => {
        if (resumeDrawer?.blobUrl) URL.revokeObjectURL(resumeDrawer.blobUrl);
        setResumeDrawer(null);
    };

    const exportCsv = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (filter !== "any") params.set("filter", filter);
            const url = backendUrl(`/api/admin/all-professionals/export.csv?${params.toString()}`);
            const token = localStorage.getItem("simonara_admin_token");
            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            const today = new Date().toISOString().slice(0, 10);
            a.download = `simonara-professionals-${filter}-${today}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
            toast.success("CSV exported.");
        } catch (err) {
            toast.error("Couldn't export the CSV.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="admin-all-professionals">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="overline">Master admin · Directory</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        All Professionals
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Every professional signup — with passport progress, resume status, last-seen, and quick nudge. Resumes are downloadable to master admin only.
                    </p>
                </div>
                <button
                    onClick={exportCsv}
                    disabled={exporting || loading || rows.length === 0}
                    className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="allpros-export-csv"
                    title="Export the current filter to CSV"
                >
                    {exporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                    Export CSV
                </button>
            </div>

            <div className="card-soft p-4 mb-4 space-y-3">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name or email…"
                        data-testid="allpros-search"
                        className="pl-9 h-10"
                    />
                </div>
                <div className="flex flex-wrap gap-2" data-testid="allpros-filter-chips">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            data-testid={`allpros-chip-${f.key}`}
                            onClick={() => setFilter(f.key)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                filter === f.key
                                    ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 font-medium"
                                    : "border-border hover:border-emerald-500/40 hover:bg-muted/30"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <User size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No professionals match this filter.</div>
                </div>
            ) : (
                <div className="grid gap-3">
                    {rows.map((row) => (
                        <div
                            key={row.user_id}
                            className="card-soft p-4 flex items-start gap-4 hover:border-emerald-500/40 transition"
                            data-testid={`allpros-row-${row.user_id}`}
                        >
                            <Avatar
                                src={row.picture}
                                name={row.name}
                                email={row.email}
                                userId={row.user_id}
                                size={48}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <div className="font-heading font-semibold text-foreground truncate">
                                        {row.name || "—"}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{row.email}</div>
                                    {row.id_verified && (
                                        <Badge className="text-[10px] bg-emerald-500/15 text-emerald-700 border border-emerald-500/30" data-testid={`allpros-verified-${row.user_id}`}>
                                            <ShieldCheck size={9} className="mr-0.5" /> ID verified
                                        </Badge>
                                    )}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                    {row.passport_desired_position && (
                                        <span className="inline-flex items-center gap-1"><Sparkles size={10} /> {row.passport_desired_position}</span>
                                    )}
                                    {row.passport_city && (
                                        <span className="inline-flex items-center gap-1"><MapPin size={10} /> {row.passport_city}</span>
                                    )}
                                    {row.passport_years != null && (
                                        <span className="inline-flex items-center gap-1">· {row.passport_years} yrs</span>
                                    )}
                                    <span className="inline-flex items-center gap-1">
                                        · Last seen: {row.last_seen_at ? new Date(row.last_seen_at).toLocaleString() : "never"}
                                    </span>
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <span
                                        className={`text-[11px] rounded-full border px-2 py-0.5 font-medium ${completionTone(row.passport_completion)}`}
                                        data-testid={`allpros-completion-${row.user_id}`}
                                    >
                                        <IdCard size={9} className="inline mr-1 mb-0.5" />
                                        {row.has_passport ? `${row.passport_completion}% passport` : "No passport"}
                                    </span>
                                    {(row.has_resume_file || row.has_resume_text) && (
                                        <span className="text-[11px] rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-700 px-2 py-0.5 font-medium">
                                            <FileText size={9} className="inline mr-1 mb-0.5" /> Resume on file
                                        </span>
                                    )}
                                    {row.trust_score > 0 && (
                                        <span className="text-[11px] rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 px-2 py-0.5 font-medium">
                                            Verified {row.trust_score}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                                <button
                                    onClick={() => setReminderDialog({ row })}
                                    className="text-xs px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 inline-flex items-center gap-1.5 transition"
                                    data-testid={`allpros-nudge-${row.user_id}`}
                                    title="Send passport reminder"
                                >
                                    <BellRing size={11} /> Nudge
                                </button>
                                {row.has_passport && (
                                    <button
                                        onClick={() => setDeleteDialog({ row })}
                                        className="text-xs px-3 py-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 inline-flex items-center gap-1.5 transition"
                                        data-testid={`allpros-delete-${row.user_id}`}
                                        title="Delete passport (24h undo window)"
                                    >
                                        <Trash2 size={11} /> Purge
                                    </button>
                                )}
                                {row.resume_id ? (
                                    <button
                                        onClick={() => openResume(row)}
                                        className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 inline-flex items-center gap-1.5 transition"
                                        data-testid={`allpros-resume-${row.user_id}`}
                                        title="View resume PDF"
                                    >
                                        <Download size={11} /> Resume
                                    </button>
                                ) : row.has_resume_text ? (
                                    <span className="text-[10px] text-muted-foreground italic">Parsed text only</span>
                                ) : null}
                                {row.linkedin_url && (
                                    <a
                                        href={row.linkedin_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs px-3 py-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 inline-flex items-center gap-1.5 transition"
                                        data-testid={`allpros-linkedin-${row.user_id}`}
                                    >
                                        <Linkedin size={11} /> LinkedIn
                                    </a>
                                )}
                                <a
                                    href={`mailto:${row.email}`}
                                    className="text-xs px-3 py-1.5 rounded-full border border-slate-500/30 bg-slate-500/10 text-slate-700 hover:bg-slate-500/20 inline-flex items-center gap-1.5 transition"
                                    data-testid={`allpros-email-${row.user_id}`}
                                >
                                    <Mail size={11} /> Email
                                </a>
                                {row.phone && (
                                    <a
                                        href={`sms:${row.phone}`}
                                        className="text-xs px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 inline-flex items-center gap-1.5 transition"
                                        data-testid={`allpros-sms-${row.user_id}`}
                                        title={`Text ${row.phone}`}
                                    >
                                        <Phone size={11} /> Text
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Page {page} of {totalPages} · {total} professionals</span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className="btn-outline text-xs px-3 py-1 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="allpros-prev"
                        >
                            <ChevronLeft size={12} /> Prev
                        </button>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className="btn-outline text-xs px-3 py-1 disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid="allpros-next"
                        >
                            Next <ChevronRight size={12} />
                        </button>
                    </div>
                </div>
            )}

            <Dialog open={!!reminderDialog} onOpenChange={(o) => !o && setReminderDialog(null)}>
                <DialogContent data-testid="allpros-nudge-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BellRing size={16} className="text-violet-600" /> Send passport reminder
                        </DialogTitle>
                        <DialogDescription>
                            Email <span className="font-semibold">{reminderDialog?.row?.email}</span> a friendly nudge to finish their Talent Passport
                            {reminderDialog?.row && (
                                <span> — currently {reminderDialog.row.passport_completion}% complete.</span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Input
                            value={reminderNote}
                            onChange={(e) => setReminderNote(e.target.value)}
                            placeholder="Optional personal note (rendered as a highlighted quote in the email)"
                            data-testid="allpros-nudge-note"
                        />
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setReminderDialog(null)}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="allpros-nudge-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={fireReminder}
                            disabled={submitting}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1"
                            data-testid="allpros-nudge-confirm"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Send reminder
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
                <DialogContent data-testid="allpros-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-600">
                            <Trash2 size={16} /> Delete this Talent Passport?
                        </DialogTitle>
                        <DialogDescription>
                            Passport for <span className="font-semibold">{deleteDialog?.row?.email}</span> will be soft-deleted immediately.
                            A 24-hour undo window applies before the record is permanently purged from MongoDB.
                            The underlying user account stays intact — only the passport row is removed.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setDeleteDialog(null)}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="allpros-delete-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={firePurge}
                            disabled={deleting}
                            className="text-sm px-4 py-2 rounded-full bg-rose-500 text-white hover:bg-rose-600 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="allpros-delete-confirm"
                        >
                            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete passport
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Inline resume preview drawer — slides in from the right, blocks
                page scroll via a backdrop. iframe renders the PDF blob URL. */}
            {resumeDrawer && (
                <div className="fixed inset-0 z-50 flex" data-testid="allpros-resume-drawer">
                    <div
                        className="flex-1 bg-black/40 backdrop-blur-sm"
                        onClick={closeResumeDrawer}
                        data-testid="allpros-resume-backdrop"
                    />
                    <aside className="w-full max-w-[900px] bg-background border-l border-border shadow-xl flex flex-col">
                        <header className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Resume preview</div>
                                <div className="font-heading font-semibold text-foreground truncate">{resumeDrawer.row.name || resumeDrawer.row.email}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {resumeDrawer.row.resume_filename || "resume.pdf"}
                                    {resumeDrawer.row.passport_desired_position && <> · {resumeDrawer.row.passport_desired_position}</>}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {resumeDrawer.blobUrl && (
                                    <a
                                        href={resumeDrawer.blobUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs px-3 py-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 inline-flex items-center gap-1.5 transition"
                                        data-testid="allpros-resume-newtab"
                                        title="Open in a new tab"
                                    >
                                        <ExternalLink size={11} /> New tab
                                    </a>
                                )}
                                <button
                                    onClick={closeResumeDrawer}
                                    className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center"
                                    data-testid="allpros-resume-close"
                                    aria-label="Close resume preview"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </header>
                        <div className="flex-1 bg-muted/20 min-h-0">
                            {resumeLoading || !resumeDrawer.blobUrl ? (
                                <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm">
                                    <Loader2 size={22} className="animate-spin text-emerald-500" />
                                    Loading resume…
                                </div>
                            ) : (
                                <iframe
                                    src={resumeDrawer.blobUrl}
                                    title="Resume PDF"
                                    className="w-full h-full border-0 bg-white"
                                    data-testid="allpros-resume-iframe"
                                />
                            )}
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
